import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DealerBadge } from "@/components/DealerBadge";
import { BadgeCheck, Check, Loader2, Store } from "lucide-react";
import { toast } from "sonner";

export default function BecomeDealer() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { api.get("/dealer/plan").then((r) => setPlan(r.data)); }, []);

  const subscribe = async () => {
    setLoading(true);
    try {
      const r = await api.post("/payments/subscribe", { origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start checkout");
      setLoading(false);
    }
  };

  if (user?.is_dealer) {
    return (
      <div className="max-w-lg mx-auto px-6 py-24 text-center">
        <BadgeCheck className="w-16 h-16 text-blaze mx-auto" strokeWidth={2} />
        <h1 className="font-heading font-900 text-3xl uppercase tracking-tight mt-6">You're a Verified Dealer</h1>
        <p className="text-sm text-muted-foreground mt-2">Your listings get priority placement and your profile shows the verified badge.</p>
        <Button onClick={() => navigate(`/seller/${user.user_id}`)} className="mt-6 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold">View my dealer profile</Button>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <div className="text-center mb-10">
        <div className="w-14 h-14 bg-blaze rounded-sm flex items-center justify-center mx-auto mb-4"><Store className="w-7 h-7 text-white" /></div>
        <h1 className="font-heading font-900 text-4xl uppercase tracking-tight">Become a Dealer</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">For shops and high-volume sellers. Stand out with a verified badge and always-on priority placement.</p>
      </div>

      <div className="bg-[#141414] border border-blaze/40 rounded-sm p-8 relative overflow-hidden">
        <div className="absolute top-0 right-0 bg-blaze text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1">Featured</div>
        <div className="flex items-center gap-2 mb-1"><DealerBadge size="lg" /></div>
        <div className="flex items-end gap-2 mt-4">
          <span className="font-heading font-900 text-5xl spec-block">${plan?.price ?? "—"}</span>
          <span className="text-muted-foreground mb-1.5">/ month</span>
        </div>
        <div className="mt-6 space-y-3">
          {(plan?.perks || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2.5 text-sm">
              <div className="w-5 h-5 bg-blaze/20 rounded-sm flex items-center justify-center shrink-0"><Check className="w-3.5 h-3.5 text-blaze" /></div>
              {p}
            </div>
          ))}
        </div>
        <Button data-testid="dealer-subscribe-btn" onClick={subscribe} disabled={loading} className="w-full mt-8 bg-blaze hover:bg-blaze-hover text-white rounded-sm font-bold h-12 gap-2">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <BadgeCheck className="w-5 h-5" />}
          Subscribe as a Dealer
        </Button>
        <p className="text-center text-xs text-muted-foreground mt-4">Recurring monthly via Stripe. Test card: 4242 4242 4242 4242</p>
      </div>
    </div>
  );
}
